import React, { Fragment } from 'react'
import type { GrowthCategory } from '@mc/api'
import { Trans } from '@mc/i18n'
import { paths } from '@mc/paths'
import { BreadcrumbBar } from 'components/Breadcrumbs'
import {
  trackAllCategoriesBreadcrumbClick,
  trackPrimaryBreadcrumbClick,
  trackSeriesBreadcrumbClick,
  trackSubcatBreadcrumbClick,
} from './events'

interface DataItem {
  copy: string | React.ReactElement
  href?: string
  onClick?: () => void
}

interface NavigationBreadcrumbBarProps {
  data: DataItem[]
}

const NavigationBreadcrumbBar = ({ data }: NavigationBreadcrumbBarProps) => (
  <BreadcrumbBar className='mc-p-2'>
    {data.map((item, index) => (
      <Fragment key={index}>
        <BreadcrumbBar.Item href={item.href} onClick={item.onClick}>
          {item.copy}
        </BreadcrumbBar.Item>
        {index < data.length - 1 && <BreadcrumbBar.Separator />}
      </Fragment>
    ))}
  </BreadcrumbBar>
)

interface CategoryBreadcrumbBarProps {
  episode?: { slug: string; title: string }
  course: { slug: string; title: string }
  category: GrowthCategory
  subcategory: GrowthCategory
}

export const CategoryBreadcrumbBar = ({
  episode,
  course,
  category,
  subcategory,
}: CategoryBreadcrumbBarProps) => {
  const slugToTrack = episode ? episode.slug : course.slug

  if (!category || !subcategory) return <></>

  const data: DataItem[] = [
    {
      copy: category.name,
      href: paths.category(category.slug),
      onClick: () => {
        trackPrimaryBreadcrumbClick(slugToTrack, category.name, !!episode)
      },
    },
    {
      copy: subcategory.name,
      href: paths.subcategoryPage(subcategory.slug),
      onClick: () => {
        trackSubcatBreadcrumbClick(slugToTrack, subcategory.name, !!episode)
      },
    },
    {
      copy: course.title,
      href: episode ? paths.courseTypePage('course', course.slug) : undefined,
      onClick: () => {
        if (!episode) return
        trackSeriesBreadcrumbClick(slugToTrack, course.title, !!episode)
      },
    },
  ]

  if (episode) {
    data.push({
      copy: episode.title,
    })
  }

  data.unshift({
    copy: <Trans i18nKey='common.categoryBreadcrumbs.allCats' ns='@mc/next' />,
    href: paths.categories,
    onClick: () => {
      trackAllCategoriesBreadcrumbClick(!!episode)
    },
  })

  return <NavigationBreadcrumbBar data={data} />
}
